import {
  type ReactNode,
  type HTMLAttributes,
  forwardRef,
  type ButtonHTMLAttributes,
  useEffect,
  useRef,
  useState,
  useId,
} from 'react';
import { createPortal } from 'react-dom';
import { QRCodeCanvas } from 'qrcode.react';

/**
 * Composants UI partagés (PIXEL — refonte v2 2026-05-20).
 *
 * Aucun composant ici n'embarque de logique métier — seulement de la mise
 * en forme accessible et cohérente avec le design system (globals.css).
 * Les sélecteurs E2E (labels, headings, rôles) sont préservés ailleurs.
 */

// ---------- Icônes inline (SVG, pas de lib tierce — RGPD §8) ----------

type IconProps = HTMLAttributes<SVGElement> & { size?: number };

const baseIcon = (size: number, children: ReactNode, props: IconProps): JSX.Element => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  >
    {children}
  </svg>
);

export const Icon = {
  Folder: ({ size = 18, ...p }: IconProps) =>
    baseIcon(size, <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />, p),
  Plus: ({ size = 18, ...p }: IconProps) =>
    baseIcon(size, (<><path d="M12 5v14" /><path d="M5 12h14" /></>), p),
  Edit: ({ size = 16, ...p }: IconProps) =>
    baseIcon(size, (<><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></>), p),
  Eye: ({ size = 16, ...p }: IconProps) =>
    baseIcon(size, (<><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" /></>), p),
  Lock: ({ size = 14, ...p }: IconProps) =>
    baseIcon(size, (<><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>), p),
  Globe: ({ size = 16, ...p }: IconProps) =>
    baseIcon(size, (<><circle cx="12" cy="12" r="10" /><path d="M2 12h20" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></>), p),
  Shield: ({ size = 16, ...p }: IconProps) =>
    baseIcon(size, <path d="M12 2 4 5v7c0 5 3.4 9.4 8 10 4.6-.6 8-5 8-10V5l-8-3z" />, p),
  Users: ({ size = 16, ...p }: IconProps) =>
    baseIcon(size, (<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>), p),
  Mail: ({ size = 16, ...p }: IconProps) =>
    baseIcon(size, (<><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 6L2 7" /></>), p),
  Lockfilled: ({ size = 14, ...p }: IconProps) =>
    baseIcon(size, (<><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>), p),
  ChevronDown: ({ size = 16, ...p }: IconProps) =>
    baseIcon(size, <path d="m6 9 6 6 6-6" />, p),
  ChevronRight: ({ size = 16, ...p }: IconProps) =>
    baseIcon(size, <path d="m9 18 6-6-6-6" />, p),
  ArrowUp: ({ size = 16, ...p }: IconProps) =>
    baseIcon(size, (<><path d="M12 19V5" /><path d="m5 12 7-7 7 7" /></>), p),
  ArrowDown: ({ size = 16, ...p }: IconProps) =>
    baseIcon(size, (<><path d="M12 5v14" /><path d="m19 12-7 7-7-7" /></>), p),
  Trash: ({ size = 16, ...p }: IconProps) =>
    baseIcon(size, (<><path d="M3 6h18" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></>), p),
  Copy: ({ size = 16, ...p }: IconProps) =>
    baseIcon(size, (<><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></>), p),
  ExternalLink: ({ size = 14, ...p }: IconProps) =>
    baseIcon(size, (<><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><path d="M15 3h6v6" /><path d="M10 14 21 3" /></>), p),
  Check: ({ size = 14, ...p }: IconProps) =>
    baseIcon(size, <path d="m5 12 5 5L20 7" />, p),
  AlertTriangle: ({ size = 16, ...p }: IconProps) =>
    baseIcon(size, (<><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><path d="M12 9v4" /><path d="M12 17h.01" /></>), p),
  Info: ({ size = 16, ...p }: IconProps) =>
    baseIcon(size, (<><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" /></>), p),
  ChartBar: ({ size = 16, ...p }: IconProps) =>
    baseIcon(size, (<><path d="M3 3v18h18" /><rect x="7" y="10" width="3" height="8" /><rect x="12" y="6" width="3" height="12" /><rect x="17" y="13" width="3" height="5" /></>), p),
  Logout: ({ size = 16, ...p }: IconProps) =>
    baseIcon(size, (<><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="m16 17 5-5-5-5" /><path d="M21 12H9" /></>), p),
  Settings: ({ size = 16, ...p }: IconProps) =>
    baseIcon(size, (<><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c.16.41.49.74.91.91H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></>), p),
};

// ---------- Badge ----------

type BadgeTone =
  | 'neutral'
  | 'primary'
  | 'info'
  | 'notice'
  | 'danger'
  | 'success'
  | 'lock';

// Map explicite : les noms de classes apparaissent en literal dans le source
// → Tailwind les voit et ne les purge pas. (Construire `badge-${tone}` à la
// volée échoue car le purge analyseur n'évalue pas les template literals.)
const BADGE_TONE_CLS: Record<BadgeTone, string> = {
  neutral: 'bg-surface-2 text-text-muted',
  primary: 'bg-primary-soft text-primary-strong',
  info: 'bg-info-soft text-info-text',
  notice: 'bg-notice-soft text-notice-text',
  danger: 'bg-danger-soft text-danger-text',
  success: 'bg-success-soft text-success-text',
  lock: 'bg-lock/10 text-lock',
};

export function Badge({
  tone = 'neutral',
  children,
  icon,
  className = '',
}: {
  tone?: BadgeTone;
  children: ReactNode;
  icon?: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium leading-5 ${BADGE_TONE_CLS[tone]} ${className}`}
    >
      {icon}
      {children}
    </span>
  );
}

// ---------- Avatar ----------

export function Avatar({ name }: { name: string }): JSX.Element {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join('');
  return <span className="avatar" aria-hidden="true">{initials || '?'}</span>;
}

// ---------- Empty state ----------

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}): JSX.Element {
  return (
    <div className="empty-state">
      {icon && <div className="mx-auto mb-3 text-text-subtle">{icon}</div>}
      <h3 className="text-base font-medium">{title}</h3>
      {description && (
        <p className="text-sm text-text-muted mt-1 max-w-md mx-auto">{description}</p>
      )}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

// ---------- Loading state ----------

export function Loading({ label = 'Chargement…' }: { label?: string }): JSX.Element {
  return (
    <div className="flex items-center gap-2 text-text-muted text-sm" role="status">
      <span className="inline-block w-3.5 h-3.5 rounded-full border-2 border-current border-r-transparent animate-spin" aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

// ---------- Page header ----------

export function PageHeader({
  title,
  subtitle,
  back,
  actions,
}: {
  title: string;
  subtitle?: string;
  back?: { to: string; label: string } | undefined;
  actions?: ReactNode;
}): JSX.Element {
  return (
    <header className="page-header">
      <div className="min-w-0">
        {back && (
          <a
            href={back.to}
            className="inline-flex items-center gap-1.5 text-sm text-text-muted hover:text-text mb-2"
          >
            <Icon.ChevronRight className="rotate-180" />
            {back.label}
          </a>
        )}
        <h1 className="page-title">{title}</h1>
        {subtitle && <p className="page-subtitle">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </header>
  );
}

// ---------- Icon button (tactile, label obligatoire pour RGAA) ----------

interface IconBtnProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  children: ReactNode;
  tone?: 'neutral' | 'danger';
}

export const IconButton = forwardRef<HTMLButtonElement, IconBtnProps>(
  function IconButton({ label, children, tone = 'neutral', className = '', ...rest }, ref) {
    const toneCls = tone === 'danger'
      ? 'text-danger hover:bg-danger-soft'
      : 'text-text-muted hover:bg-surface-2 hover:text-text';
    return (
      <button
        ref={ref}
        type="button"
        aria-label={label}
        title={label}
        className={`inline-flex items-center justify-center w-8 h-8 rounded-md transition-colors ${toneCls} ${className}`}
        style={{ minHeight: 32 }}
        {...rest}
      >
        {children}
      </button>
    );
  }
);

// ---------- Modal de confirmation (sur-validation) ----------

/**
 * Boîte de dialogue de confirmation accessible (role=dialog, focus trap léger,
 * fermeture Échap + clic backdrop). Deux niveaux :
 *  - simple : un bouton confirmer (ton neutre/danger).
 *  - fort (`confirmPhrase`) : exige la saisie EXACTE d'une phrase (ex. le titre
 *    de l'enquête) avant d'activer le bouton — garde-fou anti-suppression
 *    accidentelle (RGPD Règle 0).
 */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirmer',
  cancelLabel = 'Annuler',
  tone = 'primary',
  confirmPhrase,
  busy = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'primary' | 'danger';
  /** Si défini, l'utilisateur doit retaper cette phrase pour activer le bouton. */
  confirmPhrase?: string | undefined;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}): JSX.Element | null {
  const [typed, setTyped] = useState('');
  const titleId = useId();
  const descId = useId();

  useEffect(() => {
    if (!open) setTyped('');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const phraseOk = !confirmPhrase || typed.trim() === confirmPhrase.trim();
  const confirmBtnCls = tone === 'danger' ? 'btn-danger' : 'btn-primary';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-text/40 backdrop-blur-sm animate-fade-in"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        className="w-full max-w-md bg-surface rounded-xl border border-border shadow-elevated p-6 space-y-4"
      >
        <div className="flex items-start gap-3">
          {tone === 'danger' && (
            <span className="shrink-0 w-9 h-9 rounded-full bg-danger-soft text-danger-text inline-flex items-center justify-center">
              <Icon.AlertTriangle size={18} />
            </span>
          )}
          <div className="min-w-0">
            <h2 id={titleId} className="text-lg font-semibold">{title}</h2>
            {description && (
              <div id={descId} className="text-sm text-text-muted mt-1">{description}</div>
            )}
          </div>
        </div>

        {confirmPhrase && (
          <div className="field">
            <label className="field-label">
              Tapez <code className="text-danger-text">{confirmPhrase}</code> pour confirmer
            </label>
            <input
              type="text"
              className="input-base"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              aria-label="Confirmation par saisie"
              autoFocus
            />
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="btn-ghost" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={confirmBtnCls}
            onClick={onConfirm}
            disabled={busy || !phraseOk}
          >
            {busy ? 'En cours…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Menu d'actions (kebab) ----------

export interface MenuAction {
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  tone?: 'neutral' | 'danger';
  hidden?: boolean;
}

/**
 * Menu déroulant déclenché par un bouton kebab (⋮). Ferme au clic extérieur.
 * Le panneau est rendu via un PORTAL en `position: fixed` (ancré sous le bouton)
 * → il n'est jamais rogné par l'`overflow-hidden` des cartes parentes.
 */
export function ActionMenu({ actions, label = 'Actions' }: { actions: MenuAction[]; label?: string }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const visible = actions.filter((a) => !a.hidden);

  useEffect(() => {
    if (!open) return;
    const close = (): void => setOpen(false);
    document.addEventListener('click', close);
    // Toute manipulation du viewport ferme le menu (sa position serait périmée).
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('click', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const toggle = (): void => {
    const el = wrapRef.current;
    if (el) {
      const r = el.getBoundingClientRect();
      const W = 208; // largeur du panneau (w-52)
      const left = Math.max(8, Math.min(r.right - W, window.innerWidth - W - 8));
      setPos({ top: r.bottom + 4, left });
    }
    setOpen((v) => !v);
  };

  return (
    <div className="relative inline-block" ref={wrapRef}>
      <IconButton
        label={label}
        onClick={(e) => {
          e.stopPropagation();
          toggle();
        }}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="12" cy="5" r="1.6" />
          <circle cx="12" cy="12" r="1.6" />
          <circle cx="12" cy="19" r="1.6" />
        </svg>
      </IconButton>
      {open &&
        createPortal(
          <div
            role="menu"
            style={{ position: 'fixed', top: pos.top, left: pos.left }}
            className="w-52 bg-surface rounded-xl border border-border shadow-elevated overflow-hidden z-50 animate-fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            {visible.map((a, i) => (
              <button
                key={i}
                type="button"
                role="menuitem"
                className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2.5 hover:bg-surface-2 transition-colors ${
                  a.tone === 'danger' ? 'text-danger-text' : 'text-text'
                }`}
                onClick={() => {
                  setOpen(false);
                  a.onClick();
                }}
              >
                {a.icon}
                {a.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}

// ---------- QR code de partage ----------

/**
 * Dialogue de partage par QR code. Le QR est généré **côté client** (canvas,
 * aucun appel réseau → respecte « aucune ressource tierce » + CSP stricte).
 * Encode l'URL publique `/s/:slug` (aucun token, aucune PII). Téléchargement
 * PNG via Blob (même mécanisme que l'export).
 */
export function QrCodeDialog({
  open,
  url,
  title = 'Partager par QR code',
  onClose,
}: {
  open: boolean;
  url: string;
  title?: string;
  onClose: () => void;
}): JSX.Element | null {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  if (!open) return null;

  const download = (): void => {
    const canvas = wrapRef.current?.querySelector('canvas');
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (!blob) return;
      const u = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = u;
      a.download = 'qr-enquete.png';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(u);
    }, 'image/png');
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
    >
      <div className="card max-w-sm w-full text-center" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-base font-semibold">{title}</h2>
        <p className="text-xs text-text-muted mt-1 mb-4">
          Scannez ou imprimez ce code pour ouvrir le questionnaire (lien public anonyme).
        </p>
        <div className="flex justify-center mb-4">
          <div ref={wrapRef} className="p-3 bg-white rounded-xl border border-border">
            <QRCodeCanvas value={url} size={208} level="M" marginSize={2} />
          </div>
        </div>
        <input
          readOnly
          className="input-base text-xs font-mono mb-3"
          value={url}
          onFocus={(e) => e.currentTarget.select()}
          aria-label="Lien public de l'enquête"
        />
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={() => void navigator.clipboard.writeText(url)}
          >
            <Icon.Copy /> Copier
          </button>
          <button type="button" className="btn-secondary btn-sm" onClick={download}>
            Télécharger (PNG)
          </button>
          <button type="button" className="btn-primary btn-sm" onClick={onClose}>
            Fermer
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
